/**
 * App entry.
 *
 * Wires:
 *   (1) Token storage       — legacy-compatible `sessionToken` / `user` keys
 *                             in localStorage.
 *   (2) HttpClient + APIs   — via `createWebAdapters` from T6, which accepts
 *                             an injected TokenProvider so the HttpClient and
 *                             the auth layer share the same adapter instance.
 *   (3) QueryClient         — from `@shopista/shared/api`, one per render
 *                             tree.
 *   (4) Orchestrators       — constructed once against the root store +
 *                             adapters; provided through context so feature
 *                             screens pull them without a circular import.
 *   (5) React tree          — BrowserRouter + ApiProvider + QueryClientProvider
 *                             + TokensProvider + OrchestratorProvider
 *                             + <App tokens={…} />.
 *
 * This is the only file in apps/web that touches platform globals
 * (`document.getElementById`, `window.fetch`, `window.localStorage`). The
 * rest of apps/web flows state + side-effects through the adapters above.
 */

// T12.0.22 — Sentry init MUST be the first statement so module-load errors
// in the rest of this entry are captured. No-ops when VITE_SENTRY_DSN is
// unset (local dev). See apps/web/src/sentry.ts.
import './sentry';

// T50 — PostHog product analytics. Imported right after Sentry (side-effect-free
// on import; the actual init runs from bootstrap() once the platform flag is
// known). No-ops when VITE_POSTHOG_KEY is unset (local dev). See apps/web/src/analytics.ts.
import { initAnalytics, captureEvent, resetAnalytics } from './analytics';
import { AnalyticsIdentity } from './AnalyticsIdentity';

// T47 Phase 4 — self-host the brand type (Poppins display / Inter body) so the
// fonts are real glyphs, not a silent system fallback. The Capacitor WebView is
// offline-first; a Google Fonts CDN `<link>` would not load offline, so the
// faces ship in the bundle via `@fontsource`. Imported weights match
// `font.weight` (400/500/600/700) so headings (semibold/bold) and body
// (regular/medium) all have a real face. `--font-family-display` (Poppins) and
// `--font-family-sans` (Inter) are emitted from packages/shared/tokens.
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient, SESSION_CLEARED_EVENT } from '@shopista/shared/api';
import { ApiProvider } from '@shopista/shared/api/hooks';
import { installPreferenceBroadcast } from './lib/preferenceBroadcast';
import { generateCssRootBlock, generateDarkThemeBlock } from '@shopista/shared/tokens';
// T19 Stage 4 — minimal mobile-responsive overrides (tap targets, header
// gutter, tab scroll). Tokens-only; see responsive.css header for rationale.
import './responsive.css';
import { App } from './App';
import { createWebAdapters } from './adapters';
import { makeSecureTokenStorage } from './adapters/secureStorage';
import { checkForAppUpdate } from './adapters/appUpdate';
import { initStripeNative } from './adapters/stripeNative';
import {
  AppUpdatePrompt,
  APP_UPDATE_AVAILABLE_EVENT,
  type AppUpdateAvailableDetail,
} from './adapters/AppUpdatePrompt';
import { DeepLinkListener } from './adapters/DeepLinkListener';
import { makeTokenStorage, TokensProvider } from './auth';
import type { WebTokenStorage } from './auth';
import { bumpSessionCount } from './features/auth';
import { isNativePlatform, resolvePlatform } from './routes/nativeGoogleSignIn';
import { setFeatureIdeasHttpClient } from './features/feature-ideas/api';
import { setQuickQuestionHttpClient } from './features/quick-questions/api';
import { setSubscriptionsHttpClient } from './features/subscriptions/api';
import { setReportProblemsHttpClient } from './features/report-problems/api';
import { setAdminHttpClient } from './features/admin/api';
import { ThemeProvider } from './theme/ThemeProvider';
import { createShoppingListsOrchestrator } from './stores/shopping-lists-orchestrator';
import { createHouseholdProductsOrchestrator } from './stores/HouseholdProductsOrchestrator';
import { createRetailerOffersOrchestrator } from './stores/RetailerOffersOrchestrator';
import { OrchestratorProvider, type OrchestratorBundle } from './stores/orchestrator-context';
import { useRootStore } from './stores/root-store';
import { LocationContextProvider } from './stores/LocationContext';

// Inject design tokens as CSS custom properties on :root so primitives can
// reference them via `var(--color-bg-primary)` etc. Done once before React
// mounts so the first paint already has token values available. The tokens
// module (packages/shared/tokens) is the single source of truth for style
// values per CP5 — see docs/for-developers/ARCHITECTURE_CONTRACT.md § Code Portability.
// The `:root` block carries the light (default) palette; the
// `[data-theme="dark"]` block redefines every `--color-*` var for dark mode
// (T12.2 step 10a). ThemeProvider toggles `<html data-theme="dark">`, so the
// dark block only takes effect when the user chooses (or the OS prefers) dark.
const tokenStyleEl = document.createElement('style');
tokenStyleEl.setAttribute('data-shopista-tokens', '');
tokenStyleEl.textContent = `${generateCssRootBlock()}\n${generateDarkThemeBlock()}`;
document.head.appendChild(tokenStyleEl);

// Read API base URL from Vite env. Tests and dev override via .env.local /
// VITE_API_BASE_URL. Default falls back to the legacy dev backend port.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

// Redact a secure-store error before logging: strip anything that could carry a
// token value, keep only the error type + message shape. Defensive — the store
// errors are Keystore status strings, but never log a raw error object that
// might have captured a value somewhere in its chain.
function redactError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return typeof error === 'string' ? error : 'unknown error';
}

// Token storage first — the HttpClient needs a TokenProvider at construction.
//
// T27.2.2.2 — on the native (Capacitor) WebView the httpOnly refresh cookie is
// cross-site and drops, so we store BOTH tokens in the Android Keystore and send
// the refresh token explicitly on /refresh. The secure provider satisfies the
// same `WebTokenStorage` surface as the web one, so the rest of the auth layer
// is unchanged. On native we must `hydrate()` the Keystore into the synchronous
// token mirror BEFORE the first request — a late hydration would 401 the
// bootstrap /auth/me and fall through to /login. The hydrate is async, so the
// render is deferred into `bootstrap()` below and runs only after it resolves
// (see the end of this file). The build target (older WebView) forbids a
// top-level await, hence the async-function wrapper rather than `await` here.
//
// The storage instance itself is constructed synchronously in both branches, so
// `tokens` (and the adapters/orchestrators built from it) bind at module scope
// and stay exportable. Only the Keystore read + the React mount are async.
const isNative = isNativePlatform();
const secureTokens = isNative
  ? makeSecureTokenStorage({
      // Redacted diagnostics — key NAME only, never the token value (secrets
      // must never reach logcat). Surfaces a Keystore write/read failure that
      // the old fire-and-forget path swallowed silently (T27.2.2.2 durability
      // fix). Disk durability itself is guaranteed by the plugin's `.commit()`
      // patch, not by anything observable here.
      diagnostics: {
        onWriteError: (key, error) =>
          console.warn(`[secureTokens] write failed for ${key}:`, redactError(error)),
        onReadError: (key, error) =>
          console.warn(`[secureTokens] read failed for ${key}:`, redactError(error)),
      },
    })
  : null;
const tokens: WebTokenStorage = secureTokens ?? makeTokenStorage();

// T50 — start PostHog analytics at boot so capture begins from the first visit
// with an anonymous distinct_id (the pre-signup acquisition funnel). The
// `platform` super-property is set from Capacitor.getPlatform() (NOT the WebView
// user-agent, NOT the boolean isNativePlatform()) so every event carries one of
// android / ios / web — the two native platforms stay distinguishable, and the
// value matches Sentry's platform tag. No-ops when VITE_POSTHOG_KEY is unset
// (local dev). Runs at module scope like Sentry — it needs no async token state,
// and the anonymous funnel must not wait on the Keystore hydration in
// bootstrap(). One seed `app_opened` marks the open.
initAnalytics({ platform: resolvePlatform() });
captureEvent('app_opened');

// The quick-questions / feature-ideas / subscriptions / report-problems / admin modules are
// self-contained `fetch` clients (ADR-012 D1), not wired into the shared
// ApiAdapter. Their default token accessor reads the `sessionToken` localStorage
// key — correct on web, but null on native where the token lives only in the
// Android Keystore (the synchronous mirror behind `tokens.getAccessToken`). Point
// each at the shared TokenProvider so they read from the same source the
// HttpClient uses on each platform. On native the mirror is hydrated in
// `bootstrap()` before the first request, so this synchronous read is valid.
// CP1/CP4: nothing Capacitor-shaped crosses into the modules — the accessor is
// just a `() => string | null` injected here.

// `createWebAdapters` accepts a TokenProvider override; pass our instance so
// the HttpClient and the auth layer share state (not two parallel copies).
const adapters = createWebAdapters({
  apiBaseUrl: API_BASE_URL,
  tokens,
});

setFeatureIdeasHttpClient(adapters.api.http);
setQuickQuestionHttpClient(adapters.api.http);
setSubscriptionsHttpClient(adapters.api.http);
setReportProblemsHttpClient(adapters.api.http);
setAdminHttpClient(adapters.api.http);

// Shared QueryClient — one per render tree. Created before orchestrators so
// each orchestrator can invalidate cache entries on write.
const queryClient = createQueryClient();
// T12.5.1 (G) — register the cross-tab preference broadcaster so a preference
// mutation in one tab refreshes the Deals/Planning badges in the others.
installPreferenceBroadcast();

// BUG7 — when the HTTP client fails to refresh an access token, it clears
// tokens and dispatches `shopista:session-cleared`. Subscribe synchronously
// at app boot so the redirect happens in the same tick the cache is cleared —
// no stale-data render window. App-lifetime listener; no unmount cleanup.
window.addEventListener(SESSION_CLEARED_EVENT, () => {
  queryClient.clear();
  // T50 — reset the analytics identity so the next user on this device starts a
  // fresh anonymous distinct_id, not the signed-out person's. No-op when
  // analytics isn't initialized (key unset).
  resetAnalytics();
  window.location.assign('/login');
});

// The app-mode is purely location-derived and never persisted (T12.6 Reopen 2):
// the store initializes to `DEFAULT_APP_MODE` ('planning') and entry detection
// (useModeDetection) corrects it from GPS. No seed from storage here.

// Orchestrators — one per feature area.
const orchestrators: OrchestratorBundle = {
  ShoppingLists: createShoppingListsOrchestrator(useRootStore, adapters, queryClient),
  HouseholdProducts: createHouseholdProductsOrchestrator(useRootStore, adapters, queryClient),
  RetailerOffers: createRetailerOffersOrchestrator(useRootStore, adapters, queryClient),
};

// Expose for downstream orchestrators + tests.
export { adapters, orchestrators, queryClient, tokens };

// Bootstrap — on native, await the Keystore hydration so the synchronous token
// mirror is populated before React mounts (and thus before the HttpClient issues
// its first request). On web there is no Keystore, so this resolves immediately.
// Wrapping the await in an async function avoids a top-level await, which the
// older-WebView build target (deliberately set; do not raise it) rejects.
async function bootstrap(): Promise<void> {
  if (secureTokens) {
    await secureTokens.hydrate();

    // T27.2.2.2 durability fix — commit pending Keystore writes to disk BEFORE
    // the OS can reclaim a backgrounded process. The previous fire-and-forget
    // writes could be SIGKILLed (lowmemorykiller) mid-flight, losing the token
    // and forcing re-login on the next cold start. On `App` backgrounding we
    // await `flush()`; because the patched plugin now persists via `.commit()`
    // (synchronous-to-disk), a settled `flush()` means the tokens are durably
    // on disk while we still have a live process. Dynamically imported so the
    // web bundle never eagerly loads the native plugin (CP1).
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          void secureTokens.flush();
        }
      });
      // `pause` fires on the way to the background on Android even when
      // `appStateChange` is throttled; flush on both for belt-and-suspenders.
      App.addListener('pause', () => {
        void secureTokens.flush();
      });
    } catch (error) {
      console.warn('[secureTokens] could not wire App lifecycle flush:', redactError(error));
    }
  }

  // T27.2.9 — Android update check (layered). The prod app bundles the web build
  // into the APK, so a WebView reload can't pull new native content — only a
  // store update can. On native we run two detectors: a self-hosted
  // `mobile-latest.json` compared to the baked versionCode (primary, fast), and
  // `getAppUpdateInfo()` (secondary, Play API). Either reporting a newer build
  // raises APP_UPDATE_AVAILABLE_EVENT so <AppUpdatePrompt> shows the dismissible
  // "Update in Play Store" nudge (one predictable tap → market:// deep-link; the
  // install is Play's, never a self-download). `checkForAppUpdate()` no-ops off
  // native Android, so the call is platform-free here and on web (the T29
  // freshness nudge stays the web path). Diagnostics surface a broken detector
  // (no silent swallow). We fire it once at launch and again on each resume, so
  // a build published while the app sits backgrounded is picked up on return.
  if (isNative) {
    const runUpdateCheck = (): void => {
      void checkForAppUpdate({
        diagnostics: {
          onCheckError: (detector, error) =>
            console.warn(`[appUpdate] ${detector} detector failed:`, redactError(error)),
        },
      }).then((result) => {
        if (!result.updateAvailable) return;
        window.dispatchEvent(
          new CustomEvent<AppUpdateAvailableDetail>(APP_UPDATE_AVAILABLE_EVENT, {
            detail: {
              // Dismissal keys on the NEWER advertised versionCode from the
              // primary, so a dismissed nudge re-appears only when a higher
              // versionCode is advertised. When only the secondary (Play API)
              // fired it surfaces no number — null leaves the nudge un-dismissable
              // (always shown) rather than wrongly suppressing future builds.
              versionCode: result.latestVersionCode,
              versionName: result.latestVersionName,
            },
          }),
        );
      });
    };
    runUpdateCheck();
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) runUpdateCheck();
      });
    } catch (error) {
      console.warn('[appUpdate] could not wire resume update check:', redactError(error));
    }

    // T35.7.2.2 native checkout — initialize the native Stripe SDK with the
    // publishable key so the OS Google/Apple Pay sheet is ready when a Stripe
    // checkout starts. NO-OP on a keyless tier (no VITE_STRIPE_PUBLISHABLE_KEY →
    // returns false; the WebView falls back to the web Payment Element, mirroring
    // StripePaymentElement's placeholder gate). Fire-and-forget: a checkout also
    // re-ensures init before presenting, so a missed/slow init here never breaks
    // the sheet — this just warms it. Diagnostics surface a redacted init error.
    void initStripeNative({
      diagnostics: {
        onError: (step, error) =>
          console.warn(`[stripeNative] ${step} failed:`, redactError(error)),
      },
    });
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root not found');
  }

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ApiProvider
          api={adapters.api}
          storage={adapters.storage}
          isOnline={() => navigator.onLine}
        >
          <TokensProvider tokens={tokens}>
            {/* T50 — analytics identity bridge. Renders nothing; identifies the
                PostHog visitor with the canonical backend user id on auth (and
                merges the anonymous pre-signup history). Inside the API + query
                providers (it reads /auth/me), above the router (route-agnostic).
                No-op when analytics isn't initialized. */}
            <AnalyticsIdentity tokens={tokens} />
            <ThemeProvider>
              <OrchestratorProvider orchestrators={orchestrators}>
                {/* T39 — the session-only LocationContext (GPS-derived
                    where-am-I, never persisted). Sits above the router so both
                    the shell's useModeDetection (writer) and Deals (reader) share
                    one instance. */}
                <LocationContextProvider>
                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <App tokens={tokens} />
                    {/* T27.2.11 — native-only Android App Links router. Routes an
                      inbound https App Link (appUrlOpen) into the SPA so a tapped
                      link lands on the matching in-app screen. Inside the router
                      (it calls useNavigate); no-op on web. */}
                    <DeepLinkListener />
                    {/* T27.2.11 Part B — the global InstallAppBanner is retired
                      (unmounted): the mobile sign-up funnel at the /login +
                      /register entry now routes Android-browser guests into the
                      app, so a separate steering banner would double-prompt. The
                      component file is kept (unused) for history. */}
                    {/* T27.2.9 — native Android "Update in Play Store" nudge.
                      Inert until checkForAppUpdate() raises
                      APP_UPDATE_AVAILABLE_EVENT (native, a newer versionCode on
                      the track); no-op surface on web/iOS. */}
                    <AppUpdatePrompt />
                  </BrowserRouter>
                </LocationContextProvider>
              </OrchestratorProvider>
            </ThemeProvider>
          </TokensProvider>
        </ApiProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// T48 — count this app open as a session. The confirm-email nudge shows only
// from the SECOND session onward, so a brand-new user reaches the app on their
// first open unbothered. One increment per page load (not per render).
bumpSessionCount();

void bootstrap();
